import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import OfferDocumentStructure from "./offer-document-structure";
import OfferDocumentPreview from "./offer-document-preview";
import OfferDocumentLivePreview from "./offer-document-live-preview";
import OfferDocumentBlockEditor from "./offer-document-block-editor";
import OfferSimpleDocumentTab from "./offer-simple-document-tab";
import {
  createCustomTextBlock,
  normalizeDocumentStructure,
  reindexDocumentBlocks,
  serializeDocumentStructure,
} from "./offer-document-structure-defaults";
import { getOfferAsync, updateOfferAsync } from "../../../slices/offer";

import { createOfferSaveQueue, registerOfferSave } from "./utils/offer-save-queue";
import { isSimpleOfferLayout } from "./utils/offer-layout";
import { getPdfOffer } from "../../../api/offer";
import { resolveOfferActionErrorMessage } from "./utils";
import toast from "react-hot-toast";

const STRUCTURE_SAVE_DEBOUNCE_MS = 800;
/** Default = live API PDF; HTML mock remains behind a small toggle. */
const PREVIEW_MODE_PDF = "pdf";
const PREVIEW_MODE_HTML = "html";

/**
 * Dokument tab — outgoing offer document structure (sidebar + A4 preview).
 * Simple layout (no persisted document_structure) uses OfferSimpleDocumentTab.
 * Generic DocumentManager attachments stay on the Vedlegg tab.
 * Cover-page fields remain on OfferCoverPage when the cover block is selected.
 */
export default function OfferDocumentTab({ offer }) {
  if (isSimpleOfferLayout(offer)) {
    return <OfferSimpleDocumentTab offer={offer} />;
  }

  return <OfferDocumentBuilderTab offer={offer} />;
}

function OfferDocumentBuilderTab({ offer }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [structure, setStructure] = useState(() =>
    normalizeDocumentStructure(offer?.document_structure, t, offer)
  );
  const [selectedBlockId, setSelectedBlockId] = useState(
    () => structure.blocks[0]?.id || null
  );
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [structureSaveState, setStructureSaveState] = useState("idle");
  const [coverSaveState, setCoverSaveState] = useState("idle");
  const [previewMode, setPreviewMode] = useState(PREVIEW_MODE_PDF);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [switchingToSimple, setSwitchingToSimple] = useState(false);
  const switchedToSimpleRef = useRef(false);
  const saveState = useMemo(() => {
    const states = [structureSaveState, coverSaveState];
    if (states.includes("error")) return "error";
    if (states.includes("saving") || states.includes("pending")) {
      return states.includes("saving") ? "saving" : "pending";
    }
    if (states.includes("saved")) return "saved";
    return "idle";
  }, [structureSaveState, coverSaveState]);
  const previewDirty = saveState === "pending" || saveState === "saving";

  const structureRef = useRef(structure);
  const persistTimerRef = useRef(null);
  const persistRef = useRef(null);
  const pdfRequestIdRef = useRef(0);
  const prevSaveStateRef = useRef(saveState);
  const refreshLivePdfRef = useRef(null);
  const saveQueue = useMemo(
    () => createOfferSaveQueue((value) => persistRef.current(value), setStructureSaveState),
    [offer?.id]
  );
  useEffect(() => registerOfferSave(offer?.id, saveQueue), [offer?.id, saveQueue]);
  const hydratedOfferIdRef = useRef(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    structureRef.current = structure;
  }, [structure]);

  // Hydrate once per offer id so getOffer refreshes don't wipe in-progress edits.
  useEffect(() => {
    if (!offer?.id) return;
    if (hydratedOfferIdRef.current === offer.id) return;
    dirtyRef.current = false;
    const next = normalizeDocumentStructure(offer.document_structure, t, offer);
    setStructure(next);
    structureRef.current = next;
    setSelectedBlockId(next.blocks[0]?.id || null);
    hydratedOfferIdRef.current = offer.id;
  }, [offer?.id, offer?.document_structure, offer, t]);

  // Soft sync from server when local state is pristine (e.g. first API payload arrives late).
  useEffect(() => {
    if (!offer?.id || dirtyRef.current) return;
    if (hydratedOfferIdRef.current !== offer.id) return;
    const server = offer.document_structure;
    if (!server?.blocks?.length) return;
    const next = normalizeDocumentStructure(server, t, offer);
    if (JSON.stringify(next.blocks) === JSON.stringify(structureRef.current.blocks)) {
      return;
    }
    setStructure(next);
    structureRef.current = next;
    setSelectedBlockId((prev) =>
      next.blocks.some((b) => b.id === prev) ? prev : next.blocks[0]?.id || null
    );
  }, [offer?.id, offer?.document_structure, offer, t]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const persistCover = useCallback(
    async (values) => {
      if (!offer?.id || switchedToSimpleRef.current) return;
      const result = await dispatch(
        updateOfferAsync({
          id: offer.id,
          cover_page_active: values.cover_page_active,
          cover_page_title: values.cover_page_title,
          cover_page_description: values.cover_page_description,
          cover_page_image_id: values.cover_page_image_id,
        })
      );
      if (switchedToSimpleRef.current) return;
      if (updateOfferAsync.rejected.match(result)) {
        throw new Error("cover_save_failed");
      }
      dispatch(getOfferAsync(offer.id));
    },
    [dispatch, offer?.id]
  );
  const coverQueue = useMemo(
    () => createOfferSaveQueue((value) => persistCover(value), setCoverSaveState),
    [persistCover]
  );
  useEffect(() => registerOfferSave(offer?.id, coverQueue), [offer?.id, coverQueue]);
  const scheduleCoverPersist = useCallback(
    (values, delay = 700) => coverQueue.schedule(values, delay),
    [coverQueue]
  );

  const persistStructure = useCallback(
    async (nextStructure) => {
      if (!offer?.id || switchedToSimpleRef.current) return;
      const payload = serializeDocumentStructure(nextStructure);
      const result = await dispatch(
        updateOfferAsync({
          id: offer.id,
          document_structure: payload,
        })
      );
      if (switchedToSimpleRef.current) return;
      if (!updateOfferAsync.rejected.match(result)) {
        if (structureRef.current === nextStructure) dirtyRef.current = false;
      } else {
        throw new Error("document_save_failed");
      }
    },
    [dispatch, offer?.id]
  );

  persistRef.current = persistStructure;
  const scheduleStructurePersist = useCallback((next, delay = STRUCTURE_SAVE_DEBOUNCE_MS) => saveQueue.schedule(next, delay), [saveQueue]);
  const flushDocumentSaves = useCallback(async () => {
    await Promise.all([saveQueue.flush(), coverQueue.flush()]);
  }, [saveQueue, coverQueue]);

  const refreshLivePdf = useCallback(async () => {
    if (!offer?.id) return;
    const requestId = ++pdfRequestIdRef.current;
    setPdfLoading(true);
    setPdfError(null);
    try {
      await flushDocumentSaves();
      if (requestId !== pdfRequestIdRef.current) return;
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: "document" },
      });
      if (requestId !== pdfRequestIdRef.current) return;
      if (!response?.pdf_url) {
        const message = resolveOfferActionErrorMessage(
          t,
          response,
          "offers.documentBuilder.preview.pdf_failed"
        );
        setPdfError(message);
        return;
      }
      setPdfUrl(response.pdf_url);
      setPdfError(null);
    } catch (err) {
      if (requestId !== pdfRequestIdRef.current) return;
      setPdfError(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    } finally {
      if (requestId === pdfRequestIdRef.current) {
        setPdfLoading(false);
      }
    }
  }, [flushDocumentSaves, offer?.id, t]);

  refreshLivePdfRef.current = refreshLivePdf;

  // Initial load + reset when switching offers. No per-keystroke regeneration.
  useEffect(() => {
    if (!offer?.id) return undefined;
    setPdfUrl("");
    setPdfError(null);
    refreshLivePdfRef.current?.();
    return () => {
      pdfRequestIdRef.current += 1;
    };
  }, [offer?.id]);

  // Auto-refresh only after debounce save completes (pending/saving → saved).
  useEffect(() => {
    const prev = prevSaveStateRef.current;
    prevSaveStateRef.current = saveState;
    if (previewMode !== PREVIEW_MODE_PDF) return;
    if (saveState !== "saved") return;
    if (prev !== "saving" && prev !== "pending") return;
    refreshLivePdfRef.current?.();
  }, [saveState, previewMode]);

  const openPdf = async () => {
    try {
      await flushDocumentSaves();
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: "document" },
      });
      if (!response?.pdf_url) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            response,
            "offers.documentBuilder.preview.pdf_failed"
          )
        );
        return;
      }
      window.open(response.pdf_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    }
  };

  /**
   * Switch back to simple sheet: clears persisted document_structure via
   * PATCH layout=simple. Confirm first — block structure is not recoverable.
   * Cancel pending structure writes so unmount flush cannot resurrect them.
   */
  const switchToSimple = async () => {
    if (!offer?.id || switchingToSimple) return;
    if (!window.confirm(t("offers.documentBuilder.switch_to_simple_confirm"))) {
      return;
    }
    setSwitchingToSimple(true);
    switchedToSimpleRef.current = true;
    dirtyRef.current = false;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    saveQueue.cancel();
    coverQueue.cancel();
    try {
      // Wait out any in-flight structure write, then clear as the final write.
      await Promise.all([saveQueue.flush(), coverQueue.flush()]);
      const result = await dispatch(
        updateOfferAsync({ id: offer.id, layout: "simple" })
      );
      if (updateOfferAsync.rejected.match(result)) {
        switchedToSimpleRef.current = false;
        toast.error(t("offers.documentBuilder.switch_to_simple_failed"));
        return;
      }
      await dispatch(getOfferAsync(offer.id));
      toast.success(t("offers.documentBuilder.switch_to_simple_success"));
    } catch {
      switchedToSimpleRef.current = false;
      toast.error(t("offers.documentBuilder.switch_to_simple_failed"));
    } finally {
      setSwitchingToSimple(false);
    }
  };

  const updateStructure = useCallback(
    (updater, { persist = true } = {}) => {
      dirtyRef.current = true;
      setStructure((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        structureRef.current = next;
        if (persist) scheduleStructurePersist(next);
        return next;
      });
    },
    [scheduleStructurePersist]
  );

  const selectedBlock = useMemo(
    () => structure.blocks.find((b) => b.id === selectedBlockId) || null,
    [structure.blocks, selectedBlockId]
  );

  const handleSelectBlock = useCallback((blockId) => {
    setSelectedBlockId(blockId);
    setInspectorOpen(true);
  }, []);

  const handleAddBlock = useCallback(() => {
    updateStructure((prev) => {
      const block = createCustomTextBlock(t, prev.blocks.length);
      const blocks = reindexDocumentBlocks([...prev.blocks, block]);
      setSelectedBlockId(block.id);
      setInspectorOpen(true);
      return { ...prev, version: 1, blocks };
    });
  }, [t, updateStructure]);

  const handleMove = useCallback(
    (blockId, direction) => {
      updateStructure((prev) => {
        const index = prev.blocks.findIndex((b) => b.id === blockId);
        if (index < 0) return prev;
        const swapWith = index + direction;
        if (swapWith < 0 || swapWith >= prev.blocks.length) return prev;
        const nextBlocks = [...prev.blocks];
        [nextBlocks[index], nextBlocks[swapWith]] = [
          nextBlocks[swapWith],
          nextBlocks[index],
        ];
        return {
          ...prev,
          blocks: reindexDocumentBlocks(nextBlocks),
        };
      });
    },
    [updateStructure]
  );

  /**
   * Generic block field/content patch used by the inspector drawer.
   * `patch.content` (if present) is shallow-merged into the block's content
   * so callers only send the keys they're changing. Free-text fields (body,
   * title, notes, …) pass a longer `debounceMs`; toggles/selects persist
   * faster; `immediate: true` (e.g. template insert) persists right away.
   */
  const handleBlockPatch = useCallback(
    (blockId, patch, { debounceMs = STRUCTURE_SAVE_DEBOUNCE_MS, immediate = false } = {}) => {
      dirtyRef.current = true;
      setStructure((prev) => {
        const next = {
          ...prev,
          blocks: prev.blocks.map((b) => {
            if (b.id !== blockId) return b;
            const { content: contentPatch, ...topPatch } = patch || {};
            return {
              ...b,
              ...topPatch,
              content: contentPatch ? { ...b.content, ...contentPatch } : b.content,
            };
          }),
        };
        structureRef.current = next;
        if (immediate) {
          if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
          saveQueue.schedule(next, 0);
        } else {
          scheduleStructurePersist(next, debounceMs);
        }
        return next;
      });
    },
    [persistStructure, scheduleStructurePersist]
  );

  if (!offer?.id) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
        <DocumentTextIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">
          {t("offers.tabs.document")}
        </p>
        <p className="max-w-md text-sm text-slate-500">
          {t("offers.tabs.no_offer")}
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="offer-workspace-tools offer-document-toolbar">
        <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
          {t("offers.workspace.layout_document")}
        </span>
        <button
          type="button"
          onClick={() => refreshLivePdf()}
          disabled={pdfLoading || previewMode !== PREVIEW_MODE_PDF || switchingToSimple}
        >
          {t("offers.documentBuilder.preview.refresh")}
        </button>
        <button type="button" onClick={openPdf} disabled={switchingToSimple}>
          {t("offers.workspace.preview")}
        </button>
        <button
          type="button"
          onClick={() =>
            setPreviewMode((mode) =>
              mode === PREVIEW_MODE_PDF ? PREVIEW_MODE_HTML : PREVIEW_MODE_PDF
            )
          }
          disabled={switchingToSimple}
        >
          {previewMode === PREVIEW_MODE_PDF
            ? t("offers.documentBuilder.preview.show_html_mock")
            : t("offers.documentBuilder.preview.show_live_pdf")}
        </button>
        <button
          type="button"
          onClick={switchToSimple}
          disabled={switchingToSimple}
        >
          {switchingToSimple
            ? t("offers.documentBuilder.switching_to_simple")
            : t("offers.documentBuilder.switch_to_simple")}
        </button>
        <span
          className={[
            "offer-save-chip",
            saveState === "error" ? "is-error" : "",
            saveState === "pending" || saveState === "saving" ? "is-busy" : "",
            saveState === "saved" ? "is-saved" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="status"
          aria-live="polite"
        >
          {t(`offers.workspace.save_${saveState === "idle" ? "idle" : saveState}`)}
        </span>
        {saveState === "error" ? (
          <button type="button" onClick={() => flushDocumentSaves().catch(() => {})}>
            {t("offers.workspace.retry")}
          </button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Left: structure list + add only — no nested mini editor here. */}
        <aside className="flex w-full shrink-0 flex-col border-b border-slate-200 bg-white lg:w-[250px] lg:border-b-0 lg:border-r lg:border-slate-200">
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <OfferDocumentStructure
              blocks={structure.blocks}
              selectedBlockId={selectedBlockId}
              onSelect={handleSelectBlock}
              onAddBlock={handleAddBlock}
              onMoveUp={(id) => handleMove(id, -1)}
              onMoveDown={(id) => handleMove(id, 1)}
            />
          </div>
        </aside>

        {/* Right: live PDF iframe (default) or HTML mock — stays behind inspector. */}
        <div className="relative flex min-h-[480px] min-w-0 flex-1 flex-col lg:min-h-0">
          {previewMode === PREVIEW_MODE_PDF ? (
            <OfferDocumentLivePreview
              pdfUrl={pdfUrl}
              loading={pdfLoading}
              dirty={previewDirty}
              error={pdfError}
              onRefresh={() => refreshLivePdf()}
            />
          ) : (
            <OfferDocumentPreview
              offer={offer}
              blocks={structure.blocks}
              selectedBlockId={selectedBlockId}
            />
          )}
        </div>
      </div>

      <OfferDocumentBlockEditor
        open={inspectorOpen}
        block={selectedBlock}
        offer={offer}
        onClose={() => setInspectorOpen(false)}
        onPatch={handleBlockPatch}
        onCoverAutoSave={scheduleCoverPersist}
      />
    </div>
  );
}
